import React, { useState, useEffect, useContext } from "react";
import AuthContext from "../../context/Services/Auth";
import "./Clients.css";

import Modal from "../../Components/Modal/Modal";
import Input from "../../Components/Input/Input";
import Message from "../../Components/Message/Message";
import swal from "sweetalert";

import GlobalContext from "../../context/GlobalContext";

import { MdDeleteOutline } from "react-icons/md";
import { AiOutlineEdit } from "react-icons/ai";
import { IoIosAdd } from "react-icons/io";
import { FaMapMarkedAlt } from "react-icons/fa";
import { IoDocumentTextOutline } from "react-icons/io5";
import { useTranslation } from "react-i18next";
import MapModal from "../../Components/MapModal/MapModal";

import {
	get_all_clients,
	get_client,
	create_client,
	update_client,
	delete_client,
	get_client_summary,
} from "../../context/Services/Clients";

import { formatDate } from "../../utils/Helper";

const Clients = () => {
	const { store } = useContext(GlobalContext);
	const { userData } = useContext(AuthContext);
	const { t } = useTranslation();

	const [clients, setClients] = useState([]);
	const [loading, setLoading] = useState(false);
	const [showAddModal, setShowAddModal] = useState(false);
	const [showMapModal, setShowMapModal] = useState(false);
	const [showSummaryModal, setShowSummaryModal] = useState(false);
	const [clientSummary, setClientSummary] = useState(null);
	const [selectedClientForMap, setSelectedClientForMap] = useState(null);
	const [isEditMode, setIsEditMode] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");

	const initialPayload = {
		_id: null,
		name: "",
		phone: "",
		address: "",
		city: "",
	};
	const [payload, setPayload] = useState(initialPayload);

	// Fetch all clients
	const handle_get_clients = async () => {
		try {
			const res = await get_all_clients({ query: searchQuery });
			setClients(res?.data || []);
		} catch (e) {
			console.log(e);
		}
	};

	// Delete a client
	const handle_delete_client = async (id) => {
		const willDelete = await swal({
			title: t("Êtes-vous sûr ?"),
			text: t("Voulez-vous vraiment supprimer ce client ?"),
			icon: "warning",
			buttons: true,
			dangerMode: true,
		});

		if (willDelete) {
			try {
				await delete_client(id);
				handle_get_clients();
			} catch (e) {
				console.log(e);
			}
		}
	};

	// Open edit modal with data
	const handle_open_edit = (client) => {
		setPayload({
			_id: client._id,
			name: client.name || "",
			phone: client.phone || "",
			address: client.address || "",
			city: client.city || "",
		});
		setIsEditMode(true);
		setShowAddModal(true);
	};

	// Open add modal
	const handle_open_add = () => {
		setPayload(initialPayload);
		setIsEditMode(false);
		setShowAddModal(true);
	};

	// Open map modal
	const handle_open_map = (client) => {
		setSelectedClientForMap(client);
		setShowMapModal(true);
	};

	// Open summary modal
	const handle_open_summary = async (client) => {
		try {
			setLoading(true);
			const res = await get_client_summary(client._id);
			setClientSummary(res?.data || null);
			setShowSummaryModal(true);
		} catch (e) {
			console.log(e);
			swal("Error", t("Erreur lors de la récupération du résumé"), "error");
		} finally {
			setLoading(false);
		}
	}

	// Handle form submission
	const handle_submit = async () => {
		if (!payload.name || payload.name.trim() === "") {
			swal("Error", t("Le nom du client est requis"), "error");
			return;
		}

		setLoading(true);
		try {
			if (isEditMode && payload._id) {
				await update_client(payload._id, payload);
			} else {
				await create_client(payload);
			}

			handle_get_clients();
			setPayload(initialPayload);
			setIsEditMode(false);
			setShowAddModal(false);
		} catch (e) {
			console.log(e);
			if (e?.response?.data?.message === "CLIENT_PHONE_EXISTS") {
				swal("Error", t("Un client avec ce numéro existe déjà"), "error");
			} else {
				swal("Error", e?.response?.data?.message || "Error", "error");
			}
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		handle_get_clients();
	}, [searchQuery, store]);

	return (
		<>
			<Message loading={loading} status={{ status: loading }} />

			{/* Map Modal */}
			{selectedClientForMap && (
				<MapModal
					show={showMapModal}
					onClose={() => {
						setShowMapModal(false);
						setSelectedClientForMap(null);
					}}
					clientName={selectedClientForMap.name}
					coordinates={selectedClientForMap.drop_coordinates}
					editable={userData?.role === "admin"}
					clientId={selectedClientForMap._id}
					onCoordinateAdded={async () => {
						handle_get_clients();
						// Re-fetch the selected client to refresh map markers
						if (selectedClientForMap?._id) {
							try {
								const res = await get_client(selectedClientForMap._id);
								if (res?.data) setSelectedClientForMap(res.data);
							} catch (e) {
								console.log(e);
							}
						}
					}}
				/>
			)}

			{/* Client Summary Modal */}
			<Modal
				showBakcDrop={showSummaryModal}
				modal={showSummaryModal}
				hideModal={() => {
					setShowSummaryModal(false);
					setClientSummary(null);
				}}
				className="client_summary_modal_wrapper"
			>
				<div>
					<div className="flex items-center" style={{ gap: "10px", marginBottom: "20px" }}>
						<div style={{ backgroundColor: "#EFF6FF", color: "#3B82F6", padding: "10px", borderRadius: "50%", display: "flex" }}>
							<IoDocumentTextOutline size={24} />
						</div>
						<h3 style={{ margin: 0, fontSize: "18px" }}>{t("Résumé Client")} - <span style={{ color: "#3B82F6" }}>{clientSummary?.client?.name}</span></h3>
					</div>

					{clientSummary ? (
						<div className="client_summary_grid">
							<div className="client_stat_card st_neutral">
								<span className="client_stat_label">{t("Téléphone")}</span>
								<b className="client_stat_val">{clientSummary.client?.phone || "-"}</b>
							</div>
							<div className="client_stat_card st_neutral">
								<span className="client_stat_label">{t("Adresse")}</span>
								<b className="client_stat_val" style={{ fontSize: "16px" }}>{clientSummary.client?.address || clientSummary.client?.city || "-"}</b>
							</div>
							
							<div className="two_span">
								<h4 className="client_summary_card_header">{t("Statistiques des Commandes")}</h4>
							</div>

							<div className="client_stat_card st_green">
								<span className="client_stat_label">{t("Total Bons de Livraison")}</span>
								<b className="client_stat_val">{clientSummary.summary?.total_bls || 0}</b>
							</div>
							<div className="client_stat_card st_yellow">
								<span className="client_stat_label">{t("Montant Total")}</span>
								<b className="client_stat_val">{(clientSummary.summary?.total_amount || 0).toLocaleString()} {t("MAD")}</b>
							</div>
							<div className="client_stat_card st_blue">
								<span className="client_stat_label">{t("Articles Livrés")}</span>
								<b className="client_stat_val">{clientSummary.summary?.items_shipped || 0}</b>
							</div>
							<div className="client_stat_card st_red">
								<span className="client_stat_label">{t("Articles Retournés")}</span>
								<b className="client_stat_val">{clientSummary.summary?.items_returned || 0}</b>
							</div>

							<div className="two_span" style={{ marginTop: "10px" }}>
								<div className="client_stat_card st_gray">
									<span className="client_stat_label">{t("Lieux de Livraison Enregistrés")}</span>
									<b className="client_stat_val" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
										<FaMapMarkedAlt color="#6b7280" />
										{clientSummary.summary?.coordinates?.length || 0} {t("lieux")}
									</b>
								</div>
							</div>
						</div>
					) : (
						<p className="text-center p-4">Chargement...</p>
					)}
				</div>
			</Modal>

			{/* Add/Edit Client Modal */}
			<Modal
				showBakcDrop={showAddModal}
				modal={showAddModal}
				hideModal={() => {
					setShowAddModal(false);
					setPayload(initialPayload);
					setIsEditMode(false);
				}}
				className="auto_width"
			>
				<h4 style={{ marginBottom: "15px" }}>
					{isEditMode ? t("Modifier Client") : t("Ajouter Client")}
				</h4>
				<div className="grid full">
					<div
						className="flex flex-col full two_span"
						style={{ gap: "10px", padding: "0px 0" }}
					>
						<h5>{t("Nom")} *</h5>
						<Input
							placeholder="Nom du client"
							setChange={(e) => setPayload((prev) => ({ ...prev, name: e }))}
							value={payload.name}
						/>
					</div>

					<div
						className="flex flex-col full"
						style={{ gap: "10px", padding: "0px 0" }}
					>
						<h5>{t("Téléphone")}</h5>
						<Input
							placeholder="Numéro de téléphone"
							setChange={(e) => setPayload((prev) => ({ ...prev, phone: e }))}
							value={payload.phone}
						/>
					</div>

					<div
						className="flex flex-col full"
						style={{ gap: "10px", padding: "0px 0" }}
					>
						<h5>{t("Ville")}</h5>
						<Input
							placeholder="Ville"
							setChange={(e) => setPayload((prev) => ({ ...prev, city: e }))}
							value={payload.city}
						/>
					</div>

					<div
						className="flex flex-col full two_span"
						style={{ gap: "10px", padding: "0px 0" }}
					>
						<h5>{t("Adresse")}</h5>
						<Input
							placeholder="Adresse complète"
							setChange={(e) => setPayload((prev) => ({ ...prev, address: e }))}
							value={payload.address}
						/>
					</div>
				</div>

				<button
					className="filter_button full pointer"
					onClick={handle_submit}
					style={{ paddingTop: "10px", paddingBottom: "10px" }}
				>
					{isEditMode ? t("Modifier") : t("Ajouter")}
				</button>
			</Modal>

			<div className="clients_overview latest_clients_overview shadow">
				<div className="chart_section_header flex items-center">
					<b>{t("Gestion des Clients")}</b>
					<div className="relative to-right flex gap">
						<div
							className="flex gap items-center filter_button"
							onClick={handle_open_add}
						>
							<IoIosAdd style={{ color: "#7983ff", fontSize: "1.4rem" }} />
							<b>{t("Ajouter Client")}</b>
						</div>
					</div>
				</div>

				<div className="flex flex-col gap summary_clients_section">
					<div className="flex stock_head gap">
						<div className="flex gap total_div ml_15 mt_-10">
							<p>{t("Total Clients")}</p>
							<b>{clients.length}</b>
						</div>

						<div className="flex gap search_section">
							<Input
								placeholder="Rechercher..."
								setChange={(e) => setSearchQuery(e)}
								value={searchQuery}
								customInputStyle="search_input_edited"
								customParentStyle="parent_search_input_edited"
							/>
						</div>
					</div>

					<div className="table_section table_section_overwrite">
						<table cellPadding="0" cellSpacing="0">
							<thead>
								<tr>
									<th className="table-keys table_keys_overwrite cell2">{t("Nom")}</th>
									<th className="table-keys table_keys_overwrite cell2">{t("Téléphone")}</th>
									<th className="table-keys table_keys_overwrite cell2">{t("Ville")}</th>
									<th className="table-keys table_keys_overwrite cell2">{t("Adresse")}</th>
									<th className="table-keys table_keys_overwrite cell2">{t("Date Ajout")}</th>
									<th className="table-keys table_keys_overwrite cell2">{t("Actions")}</th>
								</tr>
							</thead>
							<tbody>
								{clients.length > 0 ? (
									clients.map((client) => (
										<tr className="data-cells" key={client._id}>
											<td className="value2 cell2">
												<b>{client.name}</b>
											</td>
											<td className="value2 cell2">{client.phone || "-"}</td>
											<td className="value2 cell2">{client.city || "-"}</td>
											<td className="value2 cell2">{client.address || "-"}</td>
											<td className="value2 cell2">
												{formatDate(client.created)}
											</td>
											<td className="value2 cell2 action">
												<div className="flex gap_5 justify-center">
													<IoDocumentTextOutline
														className="action_btn edit"
														style={{ backgroundColor: "#8b5cf6" }}
														onClick={() => handle_open_summary(client)}
														title={t("Résumé Client")}
													/>
													<AiOutlineEdit
														className="action_btn edit"
														onClick={() => handle_open_edit(client)}
													/>
													{(client.drop_coordinates?.length > 0 || userData?.role === "admin") && (
														<FaMapMarkedAlt
															className="action_btn view"
															onClick={() => handle_open_map(client)}
															title={userData?.role === "admin" ? t("Gérer les lieux de livraison") : t("Voir les lieux de livraison")}
														/>
													)}
													<MdDeleteOutline
														className="action_btn delete"
														onClick={() => handle_delete_client(client._id)}
													/>
												</div>
											</td>
										</tr>
									))
								) : (
									<tr>
										<td
											colSpan={6}
											style={{ textAlign: "center", padding: "20px" }}
										>
											{t("Aucun client trouvé")}
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</>
	);
};

export default Clients;